import { isGroupBy, type GroupBy } from '@nightshift/ui-grouping';
import type { BoardCard } from '@nightshift/ui-types';

/**
 * Linkable board view state (matrix #16).
 *
 * The value review's decision was "persist and share toolbar state (URL first, named saves
 * second); no separate view-management surface" — a producer who has narrowed the board to
 * something worth talking about should be able to paste the URL, not describe the three
 * controls they touched. So grouping, sort and the filter text live in the query string, next
 * to the drawer's `ticket`.
 *
 * Density also lives in the URL (`density=` in `router.ts`), but it is not a constraint:
 * comfortable vs compact describes the monitor, not which cards are on screen.
 *
 * Defaults are written as absent params, so a board nobody has filtered has a clean
 * `/p/gridguard/board` URL.
 */

const BOARD_SORTS = ['dependency', 'updated', 'key'] as const;
export type BoardSort = (typeof BOARD_SORTS)[number];

export function isBoardSort(value: unknown): value is BoardSort {
  return typeof value === 'string' && (BOARD_SORTS as readonly string[]).includes(value);
}

export interface BoardView {
  groupBy: GroupBy;
  sortBy: BoardSort;
  filterText: string;
  /**
   * Density pinned by the URL, or null for the default.
   *
   * Density describes the screen rather than the view, so it is not part of a board's
   * constraints and does not count towards the reset affordance. It is addressable anyway
   * because an unaddressable density makes a board screenshot irreproducible.
   */
  density: string | null;
}

export const BOARD_VIEW_DEFAULT: BoardView = {
  groupBy: 'none',
  sortBy: 'dependency',
  filterText: '',
  density: null,
};

function ticketKeyCompare(left: BoardCard, right: BoardCard): number {
  return left.ticketKey.localeCompare(right.ticketKey, undefined, { numeric: true });
}

/**
 * Sort the Backlog without asking the server for a second projection.
 *
 * The board card already carries every unmet dependency. A finite depth means all of a
 * ticket's prerequisites are also present in this Backlog slice; missing, cross-project or
 * cyclic chains sit after that runnable order. Ticket key is the deterministic tie-breaker.
 */
export function sortBacklogCards(cards: readonly BoardCard[], sortBy: BoardSort): BoardCard[] {
  if (sortBy === 'updated') {
    return [...cards].sort((left, right) => left.elapsedMs - right.elapsedMs || ticketKeyCompare(left, right));
  }
  if (sortBy === 'key') return [...cards].sort(ticketKeyCompare);

  const byKey = new Map(cards.map((card) => [card.ticketKey, card]));
  const depths = new Map<string, number>();
  const visiting = new Set<string>();
  const depthOf = (card: BoardCard): number => {
    const known = depths.get(card.ticketKey);
    if (known !== undefined) return known;
    if (card.dependsOn.length === 0) {
      depths.set(card.ticketKey, 0);
      return 0;
    }
    if (visiting.has(card.ticketKey)) return Number.POSITIVE_INFINITY;
    visiting.add(card.ticketKey);
    let depth = 0;
    for (const dependency of card.dependsOn) {
      const prerequisite = byKey.get(dependency.ticketKey);
      if (!prerequisite) {
        depth = Number.POSITIVE_INFINITY;
        break;
      }
      const prerequisiteDepth = depthOf(prerequisite);
      if (!Number.isFinite(prerequisiteDepth)) {
        depth = Number.POSITIVE_INFINITY;
        break;
      }
      depth = Math.max(depth, prerequisiteDepth + 1);
    }
    visiting.delete(card.ticketKey);
    depths.set(card.ticketKey, depth);
    return depth;
  };

  for (const card of cards) depthOf(card);
  return [...cards].sort((left, right) => {
    const depth = (depths.get(left.ticketKey) ?? 0) - (depths.get(right.ticketKey) ?? 0);
    return Number.isNaN(depth) ? ticketKeyCompare(left, right) : depth || ticketKeyCompare(left, right);
  });
}

/** An unrecognised `group`/`sort` falls back to the default rather than blanking the board. */
export function boardViewFrom(input: {
  group?: string | null;
  sort?: string | null;
  q?: string | null;
  density?: string | null;
}): BoardView {
  return {
    groupBy: isGroupBy(input.group) ? input.group : BOARD_VIEW_DEFAULT.groupBy,
    sortBy: isBoardSort(input.sort) ? input.sort : BOARD_VIEW_DEFAULT.sortBy,
    filterText: typeof input.q === 'string' ? input.q : BOARD_VIEW_DEFAULT.filterText,
    density: input.density === 'compact' || input.density === 'comfortable' ? input.density : null,
  };
}

/** What the URL should carry. A default is `null`, meaning "leave the param off". */
export function boardViewToRoute(view: BoardView): {
  group: string | null;
  sort: string | null;
  q: string | null;
  density: string | null;
} {
  const filter = view.filterText.trim();
  return {
    group: view.groupBy === BOARD_VIEW_DEFAULT.groupBy ? null : view.groupBy,
    sort: view.sortBy === BOARD_VIEW_DEFAULT.sortBy ? null : view.sortBy,
    q: filter.length > 0 ? filter : null,
    density: view.density,
  };
}

export interface BoardColumnPopulation {
  key: string;
  count: number;
}

/**
 * Follow live column population without trampling a choice made by hand.
 *
 * A column starts collapsed when it is empty. After that, only a population edge changes it:
 * the last ticket leaving collapses it and the first ticket arriving opens it. With no edge,
 * the operator's current choice wins, so an empty column opened for inspection does not snap
 * shut on the next board refresh and a busy column deliberately collapsed stays out of the way.
 */
export function reconcileCollapsedColumns(
  previous: ReadonlyMap<string, number>,
  columns: readonly BoardColumnPopulation[],
  current: ReadonlySet<string>,
): Set<string> {
  const liveKeys = new Set(columns.map((column) => column.key));
  const next = new Set([...current].filter((key) => liveKeys.has(key)));

  for (const column of columns) {
    const before = previous.get(column.key);
    if (before === undefined) {
      if (column.count === 0) next.add(column.key);
      else next.delete(column.key);
    } else if (before === 0 && column.count > 0) {
      next.delete(column.key);
    } else if (before > 0 && column.count === 0) {
      next.add(column.key);
    }
  }

  return next;
}
